/**
 * FG Magnit — brand row.
 *
 * The row is a native horizontal scroller, so swipe and trackpad work before
 * this script loads. Two enhancements sit on top:
 *
 *  - arrows that page by the visible width and hide themselves when
 *    everything already fits;
 *  - an optional continuous drift. That one moves on its own, so it must be
 *    pausable: it stops on hover, on keyboard focus, when the tab is hidden,
 *    when the row scrolls out of view, and from a visible button. It never
 *    starts for a visitor who has asked for reduced motion.
 *
 * The drift works by nudging scrollLeft rather than transforming a wrapper, so
 * the scroller stays a real scroller — a person can grab it mid-drift.
 */
class FgBrandCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[ref="track"]');
    this.nav = this.querySelector('[ref="nav"]');
    this.prev = this.querySelector('[ref="prev"]');
    this.next = this.querySelector('[ref="next"]');
    this.toggle = this.querySelector('[ref="toggle"]');
    if (!this.track || !this.nav) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onScroll = this.throttle(() => this.update());

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.prev?.addEventListener('click', () => this.page(-1));
    this.next?.addEventListener('click', () => this.page(1));

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
    } else {
      window.addEventListener('resize', this.onScroll);
    }

    this.update();
    this.setUpDrift();
  }

  disconnectedCallback() {
    this.stopDrift();
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.resizeObserver?.disconnect();
    this.inView?.disconnect();
    this.reduced?.removeEventListener('change', this.onMotionChange);
  }

  throttle(fn) {
    let frame = null;
    return () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        fn();
      });
    };
  }

  get maxScroll() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  page(direction) {
    this.track.scrollBy({
      left: this.track.clientWidth * direction,
      behavior: this.reduced.matches ? 'auto' : 'smooth',
    });
    if (this.drifting !== undefined) this.pause(true);
  }

  update() {
    const overflows = this.maxScroll > 2;
    this.nav.hidden = !overflows && !this.toggle;
    if (!overflows) return;

    this.measureLap();

    if (this.prev && this.next && !this.marquee) {
      // Math.abs keeps this right in right-to-left locales, where scrollLeft is negative.
      const position = Math.abs(this.track.scrollLeft);
      this.prev.disabled = position <= 2;
      this.next.disabled = position >= this.maxScroll - 2;
    }
  }

  /* ---- continuous drift -------------------------------------------------- */

  setUpDrift() {
    if (this.dataset.autoscroll !== 'true') return;

    this.speed = Math.max(5, Number(this.dataset.speed) || 30); // px per second
    this.drifting = false;
    this.userPaused = false;

    this.onMotionChange = () => (this.reduced.matches ? this.pause(true) : this.start());
    this.reduced.addEventListener('change', this.onMotionChange);

    this.addEventListener('pointerenter', () => this.hold());
    this.addEventListener('pointerleave', () => this.release());
    this.addEventListener('focusin', () => this.hold());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.release();
    });
    this.track.addEventListener('pointerdown', () => this.pause(true), { passive: true });
    this.toggle?.addEventListener('click', () => (this.drifting ? this.pause(true) : this.resume()));

    this.onVisibility = () => (document.hidden ? this.hold() : this.release());
    document.addEventListener('visibilitychange', this.onVisibility);

    // A loop that runs while the section is off-screen is wasted work.
    if ('IntersectionObserver' in window) {
      this.inView = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.visible ? this.release() : this.hold();
      });
      // The host element is display:contents in this layout, so it generates no
      // box and would never be reported as intersecting. The track always has one.
      this.inView.observe(this.track);
    } else {
      this.visible = true;
    }

    this.start();
  }

  /** Duplicates the row once so the drift can wrap without a visible jump. */
  cloneForLoop() {
    if (this.cloned) return;
    const items = Array.from(this.track.children);
    this.half = items.length;
    items.forEach((item) => {
      const copy = item.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      copy.setAttribute('inert', '');
      this.track.appendChild(copy);
    });
    this.cloned = true;
    this.measureLap();
  }

  /**
   * The distance after which the row repeats. Half the scroll width is close
   * but not equal to it — the flex gap between the last original and the first
   * copy is counted once, which would make the wrap hitch by a few pixels every
   * lap. The offset of the first copy is the exact period.
   */
  measureLap() {
    if (!this.cloned) return;
    const first = this.track.children[0];
    const copy = this.track.children[this.half];
    this.lap = copy && first ? copy.offsetLeft - first.offsetLeft : this.track.scrollWidth / 2;
  }

  start() {
    if (this.reduced.matches || this.userPaused) return;
    if (this.maxScroll <= 2) return; // everything already fits
    this.cloneForLoop();
    this.marquee = true;
    this.track.classList.add('is-marquee');
    this.nav.hidden = false;
    this.drifting = true;
    this.setToggle();
    this.release();
  }

  tick(now) {
    if (!this.running) return;
    const elapsed = this.last ? (now - this.last) / 1000 : 0;
    this.last = now;

    // The position is accumulated here rather than read back from scrollLeft
    // each frame: a sub-pixel step can round away on write, and reading the
    // rounded value back means the row never actually leaves zero.
    this.pos += this.speed * elapsed;

    // The second half is a copy of the first, so rewinding by one period
    // lands on an identical frame.
    if (this.lap > 0 && this.pos >= this.lap) this.pos -= this.lap;
    this.track.scrollLeft = this.pos;

    this.frame = requestAnimationFrame((t) => this.tick(t));
  }

  /** A soft stop: hover, focus, hidden tab, off-screen. Undone by release(). */
  hold() {
    this.held = true;
    this.stopDrift();
  }

  release() {
    this.held = false;
    if (!this.drifting || this.userPaused || document.hidden || this.visible === false) return;
    if (this.running) return;
    this.running = true;
    this.last = 0;
    this.pos = this.track.scrollLeft;
    this.frame = requestAnimationFrame((t) => this.tick(t));
  }

  /** A hard stop: the person asked for it, so hover and focus cannot undo it. */
  pause(fromUser) {
    if (fromUser) {
      this.userPaused = true;
      this.drifting = false;
      this.setToggle();
    }
    this.stopDrift();
  }

  resume() {
    this.userPaused = false;
    this.drifting = true;
    this.setToggle();
    this.release();
  }

  stopDrift() {
    this.running = false;
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  setToggle() {
    if (!this.toggle) return;
    this.toggle.classList.toggle('is-paused', !this.drifting);
    this.toggle.setAttribute('aria-label', this.drifting ? 'Pause the brand row' : 'Play the brand row');
  }
}

if (!customElements.get('fg-brand-carousel')) {
  customElements.define('fg-brand-carousel', FgBrandCarousel);
}
